/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  List,
  ListItem,
  ListItemIcon,
  Divider,
  Alert,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  TrendingUp,
  Shield,
  Gavel,
  Star,
  Timeline,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  EmojiEvents,
  OpenInNew,
  Psychology,
  Visibility
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { getUserChallenges, Challenge, acceptHumanDefense } from '../services/challengeService';
import { getUserCubeContributions, CubeContributions } from '../services/userService';
import { useAuth } from '../contexts/AuthContext';
import { usePusherChannel, usePusherEvent } from '../hooks/usePusher';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface CubeDashboardProps {
  userId?: string;
}

const CubeDashboard: React.FC<CubeDashboardProps> = ({ userId }) => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [cubeContributions, setCubeContributions] = useState<CubeContributions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [acceptingDefense, setAcceptingDefense] = useState<string | null>(null);

  // Subscribe to user's private channel for CUBE notifications
  const userChannel = usePusherChannel(user?.id ? `user-${user.id}` : '');

  useEffect(() => {
    if (isAuthenticated) {
      loadChallenges();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loadChallenges is stable, only run when isAuthenticated changes
  }, [isAuthenticated]);

  const loadChallenges = async () => {
    try {
      setLoading(true);
      const [challengeData, contributionsData] = await Promise.all([
        getUserChallenges('all'),
        getUserCubeContributions()
      ]);
      setChallenges(challengeData);
      setCubeContributions(contributionsData);
    } catch (err) {
      setError(t('cubeDashboard.failedToLoadData', 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptDefense = async (challengeId: string) => {
    try {
      setAcceptingDefense(challengeId);
      const result = await acceptHumanDefense(challengeId);
      
      // Redirect to the new debate
      window.location.href = `/debate/${result.debateId}`;
    } catch (err: any) {
      setError(err.response?.data?.error || t('cubeDashboard.failedToAcceptDefense', 'Failed to accept defense'));
      setAcceptingDefense(null);
    }
  };

  // Handle CUBE assessment completion notifications
  const handleCubeAssessmentComplete = (data: any) => {
    if (data.successful) {
      toast.success(`CUBE Challenge Successful! ${data.refunded ? `Refunded ${data.pointsRefunded || 0} points.` : ''}`);
    } else {
      toast('CUBE Challenge Assessment Complete');
    }
    
    // Refresh challenges to show updated status
    loadChallenges();
  };

  // Handle CUBE defense ready notifications
  const handleCubeDefenseReady = (data: any) => {
    toast.success('Your CUBE challenge AI defense is ready!');
    
    // Refresh challenges to show updated status
    loadChallenges();
  };

  // Handle CUBE defense failure notifications
  const handleCubeDefenseFailed = (data: any) => {
    if (data.pointsRefunded > 0) {
      toast.success(`Refunded ${data.pointsRefunded} CUBE points`);
    }
    
    // Refresh challenges to show updated status
    loadChallenges();
  };

  // Bind to CUBE-specific Pusher events
  usePusherEvent(userChannel, 'cube_assessment_complete', handleCubeAssessmentComplete);
  usePusherEvent(userChannel, 'cube_defense_ready', handleCubeDefenseReady);
  usePusherEvent(userChannel, 'cube_defense_failed', handleCubeDefenseFailed);

  if (!isAuthenticated) {
    return (
      <Alert severity="info">
        {t('cubeDashboard.pleaseLogin', 'Please log in to view your CUBE dashboard.')}
      </Alert>
    );
  }

  const submittedChallenges = challenges.filter(c => c.relationshipType === 'submitted');
  const receivedChallenges = challenges.filter(c => c.relationshipType === 'received');
  const availableChallenges = challenges.filter(c => c.relationshipType === 'available');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'ai_defense': return 'info';
      case 'awaiting_human_defense': return 'secondary';
      case 'human_defense': return 'primary';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle />;
      case 'pending': return <HourglassEmpty />;
      case 'ai_defense': return <Timeline />;
      case 'awaiting_human_defense': return <Shield />;
      case 'human_defense': return <Gavel />;
      default: return <HourglassEmpty />;
    }
  };

  const renderChallengesList = (challengeList: Challenge[], type: 'submitted' | 'received' | 'available') => {
    if (challengeList.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
          {type === 'submitted' && t('cubeDashboard.noSubmittedChallenges', 'No submitted challenges')}
          {type === 'received' && t('cubeDashboard.noReceivedChallenges', 'No received challenges')}
          {type === 'available' && t('cubeDashboard.noAvailableChallenges', 'No available challenges')}
        </Typography>
      );
    }

    return (
      <List>
        {challengeList.map((challenge) => {
          const isCompleted = challenge.status === 'completed';
          const result = challenge.result || challenge.finalResult;
          const wasSuccessful = result === 'explanation_challenged' || result === 'explanation_updated';
          const debateId = challenge.aiDefenseDebateId || challenge.humanDefenseDebateId;
          
          return (
            <React.Fragment key={challenge._id}>
              <ListItem sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <Box sx={{ display: 'flex', width: '100%', mb: 1 }}>
                  <ListItemIcon>
                    {getStatusIcon(challenge.status)}
                  </ListItemIcon>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Primary content - topic name and chips */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      <Typography variant="subtitle2">
                        {challenge.topicName || 'Unknown Topic'}
                      </Typography>
                      <Chip 
                        size="small" 
                        label={challenge.status.replace('_', ' ')}
                        color={getStatusColor(challenge.status) as any}
                      />
                      <Chip 
                        size="small" 
                        label={challenge.challengeType}
                        variant="outlined"
                      />
                      {isCompleted && result && (
                        <Chip 
                          size="small" 
                          label={wasSuccessful ? t('cubeDashboard.challengeSuccessful', 'Challenge Successful') : t('cubeDashboard.challengeFailed', 'Challenge Failed')}
                          color={wasSuccessful ? 'success' : 'error'}
                          icon={wasSuccessful ? <CheckCircle /> : <Cancel />}
                        />
                      )}
                      {challenge.refunded && (
                        <Chip 
                          size="small" 
                          label={t('cubeDashboard.refunded', 'Refunded')}
                          color="info"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    
                    {/* Secondary content - challenge text and metadata */}
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 1, 
                        lineHeight: 1.6,
                        p: 1.5,
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.200'
                      }}
                    >
                      {challenge.challengeText}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(challenge.createdAt).toLocaleDateString()}
                      {challenge.paymentAmount > 0 && ` • ${challenge.paymentAmount} ${t('cubeDashboard.points', 'points')}`}
                      {challenge.completedAt && ` • ${t('cubeDashboard.completedOn', 'Completed')} ${new Date(challenge.completedAt).toLocaleDateString()}`}
                      {challenge.refunded && ` • ${t('cubeDashboard.refunded', 'Refunded')}`}
                    </Typography>
                  </Box>
                  {type === 'available' && challenge.status === 'awaiting_human_defense' && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleAcceptDefense(challenge._id)}
                      disabled={acceptingDefense === challenge._id}
                      startIcon={acceptingDefense === challenge._id ? <CircularProgress size={16} /> : <Shield />}
                    >
                      {t('cubeDashboard.acceptDefense', 'Accept Defense')}
                    </Button>
                  )}
                </Box>
                
                {/* AI Assessment Results for Completed Challenges */}
                {isCompleted && challenge.aiAssessment && (
                  <Box sx={{ ml: 6, mb: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.light' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Psychology color="primary" sx={{ mr: 1, fontSize: 20 }} />
                      <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>
                        {t('cubeDashboard.aiAssessmentResults', 'AI Assessment Results')}
                      </Typography>
                      {wasSuccessful && (
                        <Chip 
                          label={t('cubeDashboard.challengeSuccessful', 'Challenge Successful')} 
                          color="success" 
                          size="small" 
                          sx={{ ml: 1 }}
                          icon={<CheckCircle />}
                        />
                      )}
                      {!wasSuccessful && (
                        <Chip 
                          label={t('cubeDashboard.explanationMaintained', 'Explanation Maintained')} 
                          color="info" 
                          size="small" 
                          sx={{ ml: 1 }}
                          icon={<Shield />}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'medium' }}>
                      <strong>{t('cubeDashboard.finalDecision', 'Final Decision:')}</strong> {wasSuccessful ? t('cubeDashboard.yourChallengeWasSuccessful', 'Your challenge was successful!') : t('cubeDashboard.originalExplanationMaintained', 'The original explanation was maintained')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', lineHeight: 1.5 }}>
                                              <strong>{t('cubeDashboard.aiReasoning', 'AI Reasoning:')}</strong> "{challenge.aiAssessment.reasoning}"
                    </Typography>
                    {challenge.aiAssessment.completedAt && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                                  {t('cubeDashboard.assessmentCompleted', 'Assessment completed:')} {new Date(challenge.aiAssessment.completedAt).toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                )}
                
                {/* Action Buttons */}
                {debateId && (
                  <Box sx={{ ml: 6, display: 'flex', gap: 1 }}>
                    <Button
                      component={RouterLink}
                      to={`/debate/${debateId}`}
                      variant="outlined"
                      size="small"
                      startIcon={<Visibility />}
                    >
                      {t('cubeDashboard.viewChallengeDebate', 'View Challenge Debate')}
                    </Button>
                    {isCompleted && wasSuccessful && (
                      <Button
                        component={RouterLink}
                        to={`/debate/view/${debateId}`}
                        variant="text"
                        size="small"
                        startIcon={<OpenInNew />}
                      >
                                                  {t('cubeDashboard.publicView', 'Public View')}
                      </Button>
                    )}
                  </Box>
                )}
              </ListItem>
              <Divider />
            </React.Fragment>
          );
        })}
      </List>
    );
  };

  return (
    <Box>
      {/* CUBE Section Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" gutterBottom>
          {t('cubeDashboard.title', 'CUBE Dashboard')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('cubeDashboard.subtitle', 'Monitor your CUBE challenge activity and defend explanations')}
        </Typography>
      </Box>

      {/* Header Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Star color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" component="div">
                {user?.cubePoints || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('cubeDashboard.cubePoints', 'CUBE Points')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUp color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" component="div">
                {submittedChallenges.filter(c => {
                  const result = c.result || c.finalResult;
                  return result === 'explanation_challenged' || result === 'explanation_updated';
                }).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('cubeDashboard.successfulChallenges', 'Successful Challenges')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Shield color="info" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" component="div">
                {receivedChallenges.filter(c => {
                  const result = c.result || c.finalResult;
                  return result === 'explanation_maintained';
                }).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('cubeDashboard.defensesWon', 'Defenses Won')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <EmojiEvents color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" component="div">
                {cubeContributions?.activeCubeContributions || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('cubeDashboard.cubeContributions', 'CUBE Contributions')}
              </Typography>
              {cubeContributions && cubeContributions.totalCubeContributions > cubeContributions.activeCubeContributions && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  ({cubeContributions.totalCubeContributions} {t('cubeDashboard.total', 'total')})
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Challenge Activity */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('cubeDashboard.challengeActivity', 'Challenge Activity')}
          </Typography>
          
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
            <Tab label={`${t('cubeDashboard.submitted', 'Submitted')} (${submittedChallenges.length})`} />
            <Tab label={`${t('cubeDashboard.received', 'Received')} (${receivedChallenges.length})`} />
            <Tab label={`${t('cubeDashboard.availableToDefend', 'Available to Defend')} (${availableChallenges.length})`} />
          </Tabs>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {tabValue === 0 && renderChallengesList(submittedChallenges, 'submitted')}
              {tabValue === 1 && renderChallengesList(receivedChallenges, 'received')}
              {tabValue === 2 && renderChallengesList(availableChallenges, 'available')}
            </>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button
          component={RouterLink}
          to="/cube"
          variant="contained"
          size="large"
          sx={{ mr: 2 }}
        >
          {t('cubeDashboard.browseCube', 'Browse CUBE')}
        </Button>
        <Button
          component={RouterLink}
          to="/debates"
          variant="outlined"
          size="large"
        >
          {t('cubeDashboard.viewDebates', 'View Debates')}
        </Button>
      </Box>
    </Box>
  );
};

export default CubeDashboard; 